import axios, { AxiosError, type AxiosRequestConfig, type AxiosResponse } from "axios";
import { msalInstance, opScopes } from "@/config/authConfig";
import { graphClient } from "@/services/graph-service";
import authStore from "@/stores/auth";
import { TeamsAppAuth } from "@/stores/auth/teamAuth";

if (!window.axiosInstance) {
  window.axiosInstance = axios.create({
    headers: {
      ClientId: window.__clientId__,
      ContentType: "application/json"
    }
  });

  window.axiosInstance.interceptors.request.use(
    async (config: AxiosRequestConfig | any) => {
      config.headers["ClientId"] = window.__clientId__;

      const { user } = authStore.getState();
      if (user.isInTeams) {
        config.headers["Authorization"] = `Bearer ${user.teamsToken}`;
        try {
          if (graphClient.isInitialized()) {
            const token = await graphClient.getValidToken();
            if (token && config.headers) {
              config.headers["X-Graph-Token"] = `Bearer ${token}`;
            }
          }
        } catch (error) {
          console.error("Failed to get graph token from GraphClient:", error);
        }
        return config;
      }

      try {
        const activeAccount = msalInstance?.getActiveAccount();

        if (activeAccount) {
          // Fetch OP token
          const opToken = await msalInstance?.acquireTokenSilent({
            ...opScopes,
            account: activeAccount
          });

          if (opToken && config.headers) {
            config.headers["Authorization"] = `Bearer ${opToken.accessToken}`;
            try {
              if (graphClient.isInitialized()) {
                const token = await graphClient.getValidToken();
                if (token && config.headers) {
                  config.headers["X-Graph-Token"] = `Bearer ${token}`;
                }
              }
            } catch (error) {
              console.error("Failed to get graph token from GraphClient:", error);
            }
          }
        }
      } catch (error) {
        console.error("Failed to acquire tokens in axios interceptor:", error);
      }

      return config;
    },
    (error: any) => {
      return Promise.reject(error);
    }
  );
  window.axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async function (error: AxiosError | any) {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;
        console.log("401 error, attempting to refresh access token");
        const { user } = authStore.getState();
        if (user.isInTeams) {
          try {
            const newToken = await TeamsAppAuth.refreshAppToken();
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            return window.axiosInstance(originalRequest);
          } catch (refreshError) {
            console.error("Teams token refresh failed:", refreshError);
            return Promise.reject(refreshError);
          }
        }

        try {
          if (msalInstance) {
            const activeAccount = msalInstance.getActiveAccount();

            if (activeAccount) {
              const result = await msalInstance.acquireTokenSilent({
                ...opScopes,
                account: activeAccount,
                forceRefresh: true
              });

              if (result.accessToken) {
                originalRequest.headers["Authorization"] = `Bearer ${result.accessToken}`;
                console.log("Token refreshed successfully, retrying request");
                return window.axiosInstance(originalRequest);
              }
            }
          }
        } catch (refreshError) {
          console.error("Token refresh failed:", refreshError);
          // If refresh fails, redirect to login
          window.location.href = "/login";
          return Promise.reject(refreshError);
        }
      }

      // // Show toast notification for all errors (except successful 401 retry)
      // toastService.handleHttpError(error);

      return Promise.reject(error);
    }
  );
}

export default window.axiosInstance;
